import { Dashboard } from '@/common/dashboard/Dashboard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { DimRearrangeButton } from '@/common/entityFramework/entityPerspective/support/DimRearrangeButton';
import { DropdownSelect } from '@/common/form/formFields/DropdownSelect';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useState } from 'react';
import { IndirSaleTrendCard } from './IndirSaleTrendCard';

const metricOptions = [
  { value: 'CHBK_AMT', displayName: 'Chargeback Amount' },
  { value: 'PYMT', displayName: 'Payment' },
  { value: 'DUE_AMT', displayName: 'Due Amount' },
];

// All Entity Filter Field Definitions
const entityFilterFieldDefs = [
  {
    field: 'rcvdPeriodExpr',
    displayName: 'Received Periods',
    entity: 'PERIOD',
    code: 'PERIOD',
    module: 'sc',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INDIR_SALE_LINE',
    parentFilterFkField: 'rcvdPeriodId',
    hidden: true,
  },
  {
    field: 'paidPeriodExpr',
    displayName: 'Paid Periods',
    entity: 'PERIOD',
    code: 'PAID_PERIOD',
    module: 'sc',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INDIR_SALE_LINE',
    parentFilterFkField: 'pymtPeriodId',
    hidden: true,
  },
  {
    field: 'invPeriodExpr',
    displayName: 'Invoice Periods',
    entity: 'PERIOD',
    code: 'INV_PERIOD',
    module: 'sc',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INDIR_SALE_LINE',
    parentFilterFkField: 'invPeriodId',
  },
  {
    field: 'prodExpr',
    displayName: 'Products',
    entity: 'NDC11_PROD',
    code: 'NDC11_PROD',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INDIR_SALE_LINE',
    parentFilterFkField: 'prodId',
  },
  {
    field: 'ctrtExpr',
    displayName: 'Contracts',
    entity: 'CTRT',
    code: 'CTRT',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INDIR_SALE_LINE',
    parentFilterFkField: 'ctrtId',
  },
  {
    field: 'shipCustExpr',
    displayName: 'Ship to Customers',
    entity: 'CUST',
    code: 'SHIP_CUST',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INDIR_SALE_LINE',
    parentFilterFkField: 'shipCustId',
    hidden: true,
  },
  {
    field: 'distCustExpr',
    displayName: 'Distributor Customers',
    entity: 'CUST',
    code: 'DIST_CUST',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INDIR_SALE_LINE',
    parentFilterFkField: 'distCustId',
    hidden: true,
  },
];

// X-axis options for the dashboard charts
const xAxisOptions = [
  ...entityFilterFieldDefs.map((fieldDef) => ({ code: fieldDef.code, displayName: fieldDef.displayName, fieldDef })),
];

// All filters
const codeToFilter = Object.fromEntries(
  entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, { displayName: fieldDef.displayName }]),
);

// Default filters
const defaultVisibleFilters = entityFilterFieldDefs
  .filter((fieldDef) => !fieldDef.hidden)
  .map((fieldDef) => fieldDef.code);

export const IndirSaleFinDashboard = () => {
  const rsrc = useRsrc();

  const [defaultFormValues, setDefaultFormValues] = useState(null);
  const [visibleFilters, setVisibleFilters] = useNavStateSync('visibleFilters', defaultVisibleFilters);

  useEffect(() => {
    rsrc.data('sc', 'INDIR_SALE_DASBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      setDefaultFormValues({
        metric: ['PYMT'],
        prodExpr: null,
        ctrtExpr: null,
        shipCustExpr: null,
        distCustExpr: null,
        rcvdPeriodExpr: null,
        paidPeriodExpr: null,
        invPeriodExpr: ExprUtils.whereParams('id', res.invPeriodIds),
        invPeriodExprSelectedCount: res.invPeriodIds.length,
        invPeriodExprSelectedIds: res.invPeriodIds,
        invPeriodExprHeaderChecked: false,
        xAxis: 'INV_PERIOD',
      });
    });
  }, []);

  // Saved nav state may reference filters that no longer exist
  const activeFieldDefs = useMemo(
    () =>
      visibleFilters.map((code) => entityFilterFieldDefs.find((fieldDef) => fieldDef.code === code)).filter(Boolean),
    [visibleFilters],
  );

  const resetDims = () => {
    setVisibleFilters(defaultVisibleFilters);
  };

  if (!defaultFormValues) return null;

  const augButtons = (buttons) => {
    buttons.unshift(
      <DimRearrangeButton
        key="filterRearrangeButton"
        setDims={setVisibleFilters}
        codeToDim={codeToFilter}
        activeDims={activeFieldDefs.map(({ code }) => ({ code }))}
      />,
    );
  };

  return (
    <Dashboard
      title="Indirect Sales Financial Dashboard"
      titleIcon={fas.faFileInvoice}
      entity="INDIR_SALE"
      defaultFormValues={defaultFormValues}
      augButtons={augButtons}
      onReset={resetDims}
    >
      <FilterContainer>
        <DropdownSelect
          fieldDef={{
            field: 'metric',
            displayName: 'Metric',
            multiSelect: true,
            values: metricOptions,
          }}
        />
        {activeFieldDefs.map((fieldDef) => (
          <EntitySelect key={fieldDef.field} fieldDef={{ ...fieldDef, variant: 'outlined' }} />
        ))}
      </FilterContainer>
      <SpacerLayout>
        <Spacer weights={[2, 1]}>
          <IndirSaleTrendCard xAxisOptions={xAxisOptions} metricOptions={metricOptions} />
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
